// src/components/admin/PayoutDashboard.js
import React, { useState, useEffect } from 'react';
import { collection, getDocs, query, where } from 'firebase/firestore';
import { db } from '../../firebase';
import { DollarSign, Download, Calendar, User, TrendingUp } from 'lucide-react';

const PayoutDashboard = () => {
  const [loading, setLoading] = useState(true);
  const [payoutData, setPayoutData] = useState({
    totalOwed: 0,
    pendingPayouts: [],
    monthlyStats: {}
  });

  useEffect(() => {
    fetchPayoutData();
  }, []);

  const fetchPayoutData = async () => {
    try {
      // Get all podcasts with earnings
      const podcastsSnapshot = await getDocs(collection(db, 'podcasts'));
      const podcasts = podcastsSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));

      // Calculate payouts (mock calculation)
      let totalOwed = 0;
      const pendingPayouts = [];

      podcasts.forEach(podcast => {
        const monthlyRevenue = podcast.monetization?.monthlyRevenue || 0;
        const creatorShare = monthlyRevenue * 0.80; // 80% to creator
        
        if (creatorShare > 0) {
          totalOwed += creatorShare;
          pendingPayouts.push({
            podcastId: podcast.id,
            podcastTitle: podcast.title,
            creator: podcast.creator,
            creatorId: podcast.creatorId,
            amount: creatorShare,
            status: 'pending'
          });
        }
      });

      setPayoutData({
        totalOwed,
        pendingPayouts,
        monthlyStats: {
          totalCreators: pendingPayouts.length,
          averagePayout: pendingPayouts.length > 0 ? totalOwed / pendingPayouts.length : 0
        }
      });
      
      setLoading(false);
    } catch (error) {
      console.error('Error fetching payout data:', error);
      setLoading(false);
    }
  };

  const exportPayoutData = () => {
    const csvContent = [
      ['Skaber', 'Podcast', 'Beløb (DKK)', 'Status'],
      ...payoutData.pendingPayouts.map(payout => [
        payout.creator,
        payout.podcastTitle,
        payout.amount.toFixed(2),
        payout.status
      ])
    ].map(row => row.join(',')).join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `faircast-udbetalinger-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-400"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-white">Udbetalinger</h1>
          <p className="text-slate-400 mt-1">Håndter månedlige skaber-udbetalinger</p>
        </div>
        
        <button
          onClick={exportPayoutData}
          className="flex items-center space-x-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors"
        >
          <Download className="w-4 h-4" />
          <span>Eksporter CSV</span>
        </button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="bg-slate-800 rounded-lg p-6 border border-slate-700">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-slate-400 text-sm font-medium">Samlet Skyldig Beløb</p>
              <p className="text-2xl font-bold text-white mt-1">
                {payoutData.totalOwed.toLocaleString('da-DK')} kr
              </p>
            </div>
            <div className="p-3 rounded-lg bg-red-600/20">
              <DollarSign className="w-6 h-6 text-red-400" />
            </div>
          </div>
        </div>

        <div className="bg-slate-800 rounded-lg p-6 border border-slate-700">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-slate-400 text-sm font-medium">Afventende Udbetalinger</p>
              <p className="text-2xl font-bold text-white mt-1">
                {payoutData.pendingPayouts.length}
              </p>
            </div>
            <div className="p-3 rounded-lg bg-yellow-600/20">
              <User className="w-6 h-6 text-yellow-400" />
            </div>
          </div>
        </div>

        <div className="bg-slate-800 rounded-lg p-6 border border-slate-700">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-slate-400 text-sm font-medium">Gennemsnitlig Udbetaling</p>
              <p className="text-2xl font-bold text-white mt-1">
                {payoutData.monthlyStats.averagePayout.toLocaleString('da-DK')} kr
              </p>
            </div>
            <div className="p-3 rounded-lg bg-green-600/20">
              <TrendingUp className="w-6 h-6 text-green-400" />
            </div>
          </div>
        </div>
      </div>

      {/* Pending Payouts Table */}
      <div className="bg-slate-800 rounded-lg border border-slate-700 overflow-hidden">
        <div className="p-6 border-b border-slate-700">
          <h3 className="text-lg font-semibold text-white">Afventende Udbetalinger</h3>
          <p className="text-slate-400 text-sm mt-1">
            Månedlige udbetalinger til skabere (80% af omsætning)
          </p>
        </div>
        
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-slate-700">
              <tr>
                <th className="text-left p-4 text-slate-300 font-medium">Skaber</th>
                <th className="text-left p-4 text-slate-300 font-medium">Podcast</th>
                <th className="text-left p-4 text-slate-300 font-medium">Beløb</th>
                <th className="text-left p-4 text-slate-300 font-medium">Status</th>
                <th className="text-left p-4 text-slate-300 font-medium">Handlinger</th>
              </tr>
            </thead>
            <tbody>
              {payoutData.pendingPayouts.map((payout, index) => (
                <tr key={index} className="border-t border-slate-700 hover:bg-slate-750">
                  <td className="p-4">
                    <div className="flex items-center space-x-2">
                      <User className="w-4 h-4 text-slate-400" />
                      <span className="text-white font-medium">{payout.creator}</span>
                    </div>
                  </td>
                  <td className="p-4">
                    <span className="text-slate-300">{payout.podcastTitle}</span>
                  </td>
                  <td className="p-4">
                    <span className="text-green-400 font-semibold">
                      {payout.amount.toLocaleString('da-DK', {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2
                      })} kr
                    </span>
                  </td>
                  <td className="p-4">
                    <span className="px-2 py-1 rounded-full text-xs bg-yellow-600/20 text-yellow-400">
                      Afventer
                    </span>
                  </td>
                  <td className="p-4">
                    <button
                      className="px-3 py-1 bg-blue-600 hover:bg-blue-700 text-white text-sm rounded transition-colors"
                      disabled
                    >
                      Stripe Connect påkrævet
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {payoutData.pendingPayouts.length === 0 && (
          <div className="text-center py-12">
            <DollarSign className="w-12 h-12 text-slate-600 mx-auto mb-4" />
            <p className="text-slate-400">Ingen afventende udbetalinger</p>
          </div>
        )}
      </div>

      {/* Implementation Notice */}
      <div className="bg-blue-600/10 border border-blue-600/30 rounded-lg p-4">
        <h4 className="text-blue-400 font-medium mb-2">Implementeringsnotat</h4>
        <p className="text-blue-300 text-sm">
          For at aktivere automatiske udbetalinger skal Stripe Connect konfigureres. 
          Dette vil give mulighed for direkte bankovervørsler til skabere med automatisk skattebehandling.
        </p>
      </div>
    </div>
  );
};

export default PayoutDashboard;